import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import WorkWithUs from "../components/sections/WorkWithUs";
import { getBookingStatusBadgeMeta } from "../utils/bookingFormatters";
import { launchBookingPayment } from "../utils/paymentLauncher";

const MyBookings = () => {
  const FALLBACK_VEHICLE_IMAGE = "/assets/img/all-images/sedan-check.png";
  const [activeData, setActiveData] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
  });
  const [pastData, setPastData] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");

  const [expandedLocations, setExpandedLocations] = useState({});

  const toggleLocation = (key) => {
    setExpandedLocations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLocationText = (text, key) => {
    if (!text) return "N/A";
    const maxLength = 45;
    const isExpanded = expandedLocations[key];
    const truncatedText =
      text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    const visibleText = isExpanded ? text : truncatedText;

    return (
      <p className="mb-0" style={{ display: "inline", fontSize: "14px" }}>
        {visibleText}
        {text.length > maxLength && (
          <span
            onClick={(e) => {
              e.preventDefault();
              toggleLocation(key);
            }}
            className="text-warning ms-1 cursor-pointer fw-bold"
            style={{
              fontSize: "12px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {isExpanded ? " See less" : " See more"}
          </span>
        )}
      </p>
    );
  };

  const fetchBookings = async (type = "active", page = 1) => {
    setLoading(true);
    try {
      window.scrollTo(0, 0);
      const token = localStorage.getItem("user_token");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings?status=${type}&page=${page}`,
        { headers },
      );

      if (response.data.status) {
        if (type === "active") {
          setActiveData(response.data.bookings);
        } else {
          setPastData(response.data.bookings);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${type} bookings:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchBookings("active", 1), fetchBookings("past", 1)]);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const handlePayment = async (bookingId, payType = "full") => {
    try {
      await launchBookingPayment({ bookingId, payType });
    } catch (error) {
      window.alert(error?.message || "Unable to start payment.");
    }
  };

  const getPaidAmountDisplay = (booking) =>
    Number(
      (booking.paid_amount ??
        booking.total_customer_amount - booking.remaining_amount) ??
        booking.partial_amount,
    ).toFixed(2);

  const getPaymentNumbers = (booking) => {
    const totalAmountNum = Number(booking.total_customer_amount ?? 0);
    const paidAmountNum = Number(
      booking.paid_amount ??
        totalAmountNum - Number(booking.remaining_amount ?? 0) ??
        booking.partial_amount ??
        0,
    );
    const remainingAmountNum = Number(
      booking.remaining_due ?? booking.remaining_amount ?? 0,
    );
    const partialAmountNum = Number(booking.partial_amount ?? 0);

    return {
      totalAmountNum,
      paidAmountNum,
      remainingAmountNum,
      partialAmountNum,
      hasDue: remainingAmountNum > 0,
      isUnpaid: paidAmountNum <= 0,
    };
  };

  const renderPagination = (type, pagination) => {
    if (pagination.last_page <= 1) return null;

    const pages = [];
    const currentPage = pagination.current_page;
    const lastPage = pagination.last_page;

    pages.push(
      <button
        key="prev"
        disabled={currentPage <= 1}
        onClick={() => fetchBookings(type, currentPage - 1)}
        className="pg-btn"
      >
        ❮
      </button>,
    );

    for (let i = 1; i <= lastPage; i++) {
      if (
        i === 1 ||
        i === lastPage ||
        (i >= currentPage - 1 && i <= currentPage + 1)
      ) {
        pages.push(
          <button
            key={i}
            onClick={() => fetchBookings(type, i)}
            className={`pg-btn ${i === currentPage ? "active" : ""}`}
          >
            {i}
          </button>,
        );
      } else if (
        (i === currentPage - 2 && i > 1) ||
        (i === currentPage + 2 && i < lastPage)
      ) {
        pages.push(
          <span key={`dots-${i}`} className="pg-dots">
            ...
          </span>,
        );
      }
    }

    pages.push(
      <button
        key="next"
        disabled={currentPage >= lastPage}
        onClick={() => fetchBookings(type, currentPage + 1)}
        className="pg-btn"
      >
        ❯
      </button>,
    );

    return (
      <div id="pagination-container" className="pagination-wrapper mt-4">
        {pages}
      </div>
    );
  };

  const renderBookingCard = (booking, isPast = false) => {
    const firstRide =
      booking?.rides?.[0] ||
      booking?.trips?.[0] ||
      booking?.rides_data?.[0] ||
      {};
    const displayDate = firstRide?.date || firstRide?.ride_date;
    const rideLocations = firstRide?.locations || {};
    const pickupLocation = rideLocations?.pickup_location || firstRide?.pickup_location;
    const dropLocation =
      rideLocations?.return_drop_location ||
      rideLocations?.drop_location ||
      firstRide?.return_drop_location ||
      firstRide?.drop_location ||
      firstRide?.rides_data?.[0]?.drop_location;
    const isBookingCancelled =
      String(booking?.booking_status || "").toLowerCase() === "cancelled" ||
      String(booking?.status_name || "").toLowerCase().includes("cancel");
    const {
      paidAmountNum,
      remainingAmountNum,
      hasDue,
      isUnpaid,
    } = getPaymentNumbers(booking);
    const { label: statusLabel, badgeClass: statusBadgeClass } =
      getBookingStatusBadgeMeta(booking);
    const showPayFullButton = !isBookingCancelled && hasDue && isUnpaid;
    const showPayRemainingButton =
      !isBookingCancelled && hasDue && !isUnpaid;
    const vehicleTypeImage =
      booking?.vehicle_type_image_url ||
      firstRide?.vehicle_type_image_url ||
      FALLBACK_VEHICLE_IMAGE;

    return (
    <div className="col-lg-6 col-md-6 col-sm-12 col-12" key={booking.id}>
      <div className={`booking_col ${isPast ? "booking_colPAst" : ""} ${isBookingCancelled ? "booking_col--cancelled" : ""}`}>
        <div className="booking_left">
          <span className="booking_id">Booking Id : {booking.request_id}</span>
          <img
            className="vehicle_img"
            src={vehicleTypeImage}
            alt={firstRide?.vehicle_type_name || "Vehicle"}
          />
          <p className="trip_type">
            {booking.ride_type_name} / {booking.booking_type_name}
          </p>
          <div className="booking_chat justify-content-center">
            <>
              <a href="tel:+917535051511" className="com_icons">
                <img src="/assets/img/all-images/Phone-icon.png" alt="Call" />
              </a>
              <a
                href="https://wa.me/+917535051511"
                target="_blank"
                rel="noopener noreferrer"
                className="com_icons"
              >
                <img
                  src="/assets/img/all-images/Message-icon.png"
                  alt="WhatsApp"
                />
              </a>
            </>
          </div>
        </div>
        <div className="booking_right">
          <div className="booking_card_header mt-2">
            <p className="booking_car mb-0">
              {firstRide?.vehicle_type_name}
            </p>
            <span className={`badge ${statusBadgeClass} text-white fw-bold booking_status_badge`}>
              {statusLabel}
            </span>
          </div>
          <p className="booking_date">
            Date - {formatDate(displayDate)}
          </p>
          <div className="booking_location">
            <div className="location_col location_col2">
              <img src="/assets/img/all-images/location_on.png" alt="" />
              {renderLocationText(
                pickupLocation,
                `pickup-${booking.id}`,
              )}
            </div>
            <div className="location_col location_col2">
              <img src="/assets/img/all-images/location_on.png" alt="" />
              {renderLocationText(
                dropLocation,
                `drop-${booking.id}`,
              )}
              
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <h2 className="booking_price mb-0">
                ₹{booking.total_customer_amount}
              </h2>
              {booking.coupon_code && booking.discount_amount > 0 && (
                <small className="text-info fw-bold d-block mt-1">
                  Discount: -₹{booking.discount_amount}
                </small>
              )}
            </div>
            {paidAmountNum > 0 && hasDue && (
                <div className="text-end">
                  <small className="fw-bold text-success">
                    Paid: ₹
                    {paidAmountNum.toFixed(2)}
                  </small>
                  <br />
                  {remainingAmountNum > 0 && (
                    <small className="fw-bold text-danger">
                      Remaining: ₹{remainingAmountNum.toFixed(2)}
                    </small>
                  )}
                </div>
              )}
            {!hasDue && paidAmountNum > 0 && (
              <span className="badge bg-success text-white">
                Full Paid: ₹
                {paidAmountNum.toFixed(2)}
              </span>
            )}
          </div>

            <div className="booking_detailBtn mt-3 d-flex gap-2">
            <Link
              to={`/booking-detail/${booking.id}`}
              className="detail flex-grow-1 text-center"
            >
              Detail
            </Link>
            {showPayFullButton && (
                <button
                  type="button"
                  onClick={() => handlePayment(booking.id, "full")}
                  className="detail flex-grow-1 text-center border-0"
                >
                  Pay Full Payment
                </button>
              )}
            {showPayRemainingButton && (
                <button
                  type="button"
                  onClick={() => handlePayment(booking.id, "remaining")}
                  className="detail flex-grow-1 text-center border-0"
                >
                  Pay Remaining
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderSkeleton = () => {
    return Array.from({ length: 4 }).map((_, idx) => (
      <div className="col-lg-6 col-md-6 col-sm-12 col-12" key={idx}>
        <div className="booking_col placeholder-glow">
          <div className="booking_left">
            <span className="booking_id placeholder col-8 rounded mb-2"></span>
            <div
              className="bg-secondary bg-opacity-25 rounded mt-3 mb-3"
              style={{ height: "60px", width: "120px" }}
            ></div>
            <p className="trip_type placeholder col-10 rounded mb-0"></p>
          </div>
          <div className="booking_right">
            <p className="booking_car mt-2 placeholder col-6 rounded mb-2"></p>
            <p className="booking_date placeholder col-8 rounded mb-3"></p>
            <div className="booking_location">
              <div
                className="location_col location_col2 placeholder col-12 rounded mb-2"
                style={{ height: "20px" }}
              ></div>
              <div
                className="location_col location_col2 placeholder col-12 rounded"
                style={{ height: "20px" }}
              ></div>
            </div>
            <div className="mt-3">
              <p
                className="booking_price placeholder col-5 rounded mb-0"
                aria-hidden="true"
                style={{ height: "24px" }}
              ></p>
            </div>
            <div className="booking_detailBtn mt-3">
              <div
                className="detail w-100 placeholder rounded"
                style={{ height: "40px" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <>
      <section className="allBooking_wrap section_pb section_pt">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="bookingHeader_tabs">
                <h3>All Bookings</h3>
                <div className="booking_tabs">
                  <nav className="nav_second">
                    <div className="nav nav-tabs" id="nav-tab" role="tablist">
                      <button
                        className={`nav-link ${activeTab === "active" ? "active" : ""}`}
                        onClick={() => setActiveTab("active")}
                        type="button"
                      >
                        Active
                      </button>
                      <button
                        className={`nav-link ${activeTab === "past" ? "active" : ""}`}
                        onClick={() => setActiveTab("past")}
                        type="button"
                      >
                        Past
                      </button>
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="tab-content" id="nav-tabContent">
                <div
                  className={`tab-pane fade ${activeTab === "active" ? "active show" : ""}`}
                  role="tabpanel"
                >
                  <div className="row g-4">
                    {loading ? (
                      renderSkeleton()
                    ) : activeData.data.length > 0 ? (
                      activeData.data.map((booking) =>
                        renderBookingCard(booking, false),
                      )
                    ) : (
                      <div className="col-12 text-center p-5">
                        <h4>No active bookings found</h4>
                      </div>
                    )}
                  </div>
                  {!loading && renderPagination("active", activeData)}
                </div>
                <div
                  className={`tab-pane fade ${activeTab === "past" ? "active show" : ""}`}
                  role="tabpanel"
                >
                  <div className="row g-4">
                    {loading ? (
                      renderSkeleton()
                    ) : pastData.data.length > 0 ? (
                      pastData.data.map((booking) =>
                        renderBookingCard(booking, true),
                      )
                    ) : (
                      <div className="col-12 text-center p-5">
                        <h4>No past bookings found</h4>
                      </div>
                    )}
                  </div>
                  {!loading && renderPagination("past", pastData)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <WorkWithUs />
    </>
  );
};

export default MyBookings;
